import { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download } from "lucide-react";

/**
 * Bulk importer for CSV (.csv) and Excel (.xlsx) files.
 * - `endpoint`: POST URL accepting `file` multipart
 * - `templateEndpoint`: optional GET URL that returns the import template
 *   (will be called with ?format=xlsx and ?format=csv on button click)
 */
export default function CsvImporter({ endpoint, label, sampleHeaders, templateEndpoint, onDone, loginToggle }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [createLogins, setCreateLogins] = useState(false);

  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = loginToggle ? `${endpoint}?auto_create_logins=${createLogins}` : endpoint;
      const { data } = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const extras = [];
      if (data.updated) extras.push(`${data.updated} atualizados`);
      if (data.logins_created) extras.push(`${data.logins_created} login(s)`);
      if (data.welcome_emails_sent) extras.push(`${data.welcome_emails_sent} email(s) "define palavra-passe"`);
      const tail = extras.length ? ` · ${extras.join(" · ")}` : "";
      toast.success(`Importação (${(data.format || "csv").toUpperCase()}): ${data.created} criados · ${data.skipped} ignorados${data.errors?.length ? ` · ${data.errors.length} erros` : ""}${tail}`);
      if (data.errors?.length) console.warn("Import errors:", data.errors);
      onDone && onDone(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const downloadTemplate = async (format) => {
    if (!templateEndpoint) return;
    try {
      const r = await api.get(`${templateEndpoint}?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `consultores_template.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="kw-card p-4 flex flex-wrap items-center gap-3" data-testid={`csv-import-${endpoint.split("/").pop()}`}>
      <FileSpreadsheet className="w-5 h-5 text-[#E50000]" />
      <div className="flex-1 min-w-0">
        <div className="kw-overline">{label}</div>
        <div className="text-xs text-[#737373] truncate font-mono">Colunas: {sampleHeaders} · Formatos: CSV ou XLSX</div>
      </div>
      {templateEndpoint && (
        <>
          <button onClick={() => downloadTemplate("xlsx")} className="text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white inline-flex items-center gap-1" data-testid="download-template-xlsx">
            <Download className="w-3 h-3" /> Excel
          </button>
          <button onClick={() => downloadTemplate("csv")} className="text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] hover:text-white inline-flex items-center gap-1" data-testid="download-template-csv">
            <Download className="w-3 h-3" /> CSV
          </button>
        </>
      )}
      {loginToggle && (
        <label className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-[#A3A3A3] cursor-pointer whitespace-nowrap"
          title="Se ligado, cada novo membro com email recebe um email para definir a palavra-passe">
          <input type="checkbox" checked={createLogins} onChange={(e) => setCreateLogins(e.target.checked)}
            className="accent-[#E50000]" data-testid="csv-import-logins" />
          Criar logins + emails
        </label>
      )}
      <input ref={inputRef} type="file" accept=".csv,text/csv,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden"
        onChange={(e) => handle(e.target.files?.[0])} />
      <button onClick={() => inputRef.current?.click()} disabled={busy}
        className="kw-btn-secondary inline-flex items-center gap-2" data-testid="csv-import-btn">
        <Upload className="w-4 h-4" /> {busy ? "A importar…" : "Importar"}
      </button>
    </div>
  );
}
